import React, { useMemo, useRef, useState } from 'react';
import { Box, Button, Paper, Typography, useTheme } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { motion } from 'framer-motion';
import {
  BottomBar, MobileContainer, MobileFrame, WIDTH,
} from '../../AppLabels/shared/MobileWrapper';
import frameIphone from '../../../assets/images/frame-iphone-14.png';
import { RoletaSegment, RoletaSegmentKind } from '../../../models/roleta';
import {
  drawWeightedIndex, landingRotation, readableInk, segmentColor, sliceBounds, slicePath,
} from '../wheelGeometry';

/** Mesmo easing e duração do app (02-UX-MOBILE §animação). */
const SPIN_DURATION = 5.2;
const SPIN_EASE: [number, number, number, number] = [0.12, 0.9, 0.2, 1];

const SIZE = 196;
const CENTER = SIZE / 2;
const RADIUS = SIZE / 2 - 4;

interface Props {
  segments: RoletaSegment[];
  /** Cor do app da rede (fallback: primária do tema do admin). */
  baseColor: string;
  roletaName?: string;
  imageUrl?: string | null;
  centerImageUrl?: string | null;
}

const truncate = (value: string, max: number) => (value.length > max ? `${value.slice(0, max - 1)}…` : value);

const RoletaPreview: React.FC<Props> = ({ segments, baseColor, roletaName, imageUrl, centerImageUrl }) => {
  const theme = useTheme();
  const appColor = baseColor || theme.palette.primary.main;

  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [resultIndex, setResultIndex] = useState<number | null>(null);
  const pendingIndex = useRef<number | null>(null);

  // A roda só desenha o que o cliente veria: fatia desativada some da tela.
  // A cor automática continua vindo da posição ORIGINAL, para bater com a
  // bolinha de cor da tabela do editor.
  const wheel = useMemo(
    () => segments
      .map((segment, originalIndex) => ({ segment, originalIndex }))
      .filter((entry) => entry.segment.isEnabled),
    [segments],
  );

  const count = wheel.length;

  const handleTestSpin = () => {
    if (spinning || count < 2) return;
    const index = drawWeightedIndex(wheel.map(({ segment }) => ({ weight: segment.weight, isEnabled: true })));
    if (index < 0) return;
    pendingIndex.current = index;
    setResultIndex(null);
    setSpinning(true);
    setRotation((previous) => landingRotation(previous, index, count));
  };

  const resultSegment = resultIndex !== null ? wheel[resultIndex]?.segment : undefined;

  const resultText = (() => {
    if (!resultSegment) return null;
    switch (resultSegment.kind) {
      case RoletaSegmentKind.Prize:
        return `Você ganhou: ${resultSegment.label}`;
      case RoletaSegmentKind.ExtraSpin:
        return 'Você ganhou outro giro! Gire de novo.';
      case RoletaSegmentKind.Nothing:
        return 'Não foi dessa vez. Abasteça de novo e ganhe outro giro.';
      default:
        return resultSegment.label;
    }
  })();

  return (
    <Box>
      <Box style={{ position: 'relative', width: WIDTH }}>
        <MobileContainer>
          <Box
            sx={{
              width: '100%', bgcolor: appColor, color: '#fff', px: 1.5, py: 1,
              display: 'flex', alignItems: 'center', gap: 1, minHeight: 42,
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }} noWrap>
              {roletaName || 'Roleta da Sorte'}
            </Typography>
          </Box>

          {imageUrl && (
            <Box
              sx={{
                width: '100%', height: 52,
                backgroundImage: `url(${imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center',
              }}
            />
          )}

          <Typography sx={{ fontSize: 10, color: '#64748b', mt: 1 }}>
            Você tem <strong>1 giro</strong>
          </Typography>

          <Box sx={{ position: 'relative', width: SIZE, height: SIZE + 12, mt: 1 }}>
            {/* Ponteiro fixo no topo — a roda gira por baixo dele */}
            <Box
              sx={{
                position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                width: 0, height: 0, zIndex: 2,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: `14px solid ${appColor}`,
                filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.25))',
              }}
            />

            <motion.div
              style={{ width: SIZE, height: SIZE, marginTop: 12 }}
              animate={{ rotate: rotation }}
              transition={{ duration: SPIN_DURATION, ease: SPIN_EASE }}
              onAnimationComplete={() => {
                if (!spinning) return;
                setSpinning(false);
                setResultIndex(pendingIndex.current);
              }}
            >
              <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
                <circle cx={CENTER} cy={CENTER} r={RADIUS + 3} fill="#e2e8f0" />
                {count === 0 && (
                  <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="#f1f5f9" />
                )}
                {count === 1 && (
                  <circle
                    cx={CENTER}
                    cy={CENTER}
                    r={RADIUS}
                    fill={segmentColor(wheel[0].segment.color, appColor, wheel[0].originalIndex, Math.max(1, segments.length))}
                  />
                )}
                {count > 1 && wheel.map(({ segment, originalIndex }, index) => {
                  const { start, end } = sliceBounds(index, count);
                  const fill = segmentColor(segment.color, appColor, originalIndex, Math.max(1, segments.length));
                  const ink = readableInk(fill);
                  const center = start + (end - start) / 2;

                  return (
                    <g key={segment.id ?? `slice-${originalIndex}`}>
                      <path d={slicePath(CENTER, CENTER, RADIUS, start, end)} fill={fill} stroke="#ffffff" strokeWidth={1} />
                      <g transform={`rotate(${center} ${CENTER} ${CENTER})`}>
                        {segment.icon && (
                          <text
                            x={CENTER}
                            y={CENTER - RADIUS * 0.68}
                            textAnchor="middle"
                            fontSize={13}
                          >
                            {segment.icon}
                          </text>
                        )}
                        <text
                          x={CENTER}
                          y={CENTER - RADIUS * (segment.icon ? 0.46 : 0.56)}
                          textAnchor="middle"
                          fontSize={9}
                          fontWeight={700}
                          fill={ink}
                        >
                          {truncate(segment.label || '—', 12)}
                        </text>
                      </g>
                    </g>
                  );
                })}
                <circle cx={CENTER} cy={CENTER} r={22} fill="#ffffff" stroke="#e2e8f0" strokeWidth={2} />
                {centerImageUrl && (
                  <>
                    <clipPath id="roleta-preview-center">
                      <circle cx={CENTER} cy={CENTER} r={19} />
                    </clipPath>
                    <image
                      href={centerImageUrl}
                      x={CENTER - 19}
                      y={CENTER - 19}
                      width={38}
                      height={38}
                      clipPath="url(#roleta-preview-center)"
                      preserveAspectRatio="xMidYMid slice"
                    />
                  </>
                )}
              </svg>
            </motion.div>
          </Box>

          <Button
            size="small"
            variant="contained"
            startIcon={<ReplayIcon />}
            onClick={handleTestSpin}
            disabled={spinning || count < 2}
            sx={{ mt: 0.5, bgcolor: appColor, fontSize: 11, '&:hover': { bgcolor: appColor, opacity: 0.9 } }}
          >
            {spinning ? 'Girando…' : 'Testar giro'}
          </Button>

          <Box sx={{ minHeight: 46, px: 1.5, mt: 0.75, width: '100%' }}>
            {resultText && !spinning && (
              <Paper
                elevation={0}
                sx={{
                  p: 1, borderRadius: 2, textAlign: 'center',
                  bgcolor: '#f8fafc', border: '1px solid', borderColor: 'divider',
                }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1e293b' }}>{resultText}</Typography>
              </Paper>
            )}
            {count < 2 && (
              <Typography sx={{ fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
                Ative pelo menos 2 fatias para testar o giro.
              </Typography>
            )}
          </Box>

          <BottomBar color={appColor} />
          <MobileFrame style={{ pointerEvents: 'none', zIndex: 9 }} src={frameIphone} draggable={false} />
        </MobileContainer>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Prévia da tela do cliente. O "Testar giro" sorteia aqui no navegador, só para você conferir a aparência —
        <strong> resultado apenas ilustrativo</strong>. No app quem sorteia é o servidor.
      </Typography>
    </Box>
  );
};

export default RoletaPreview;
